import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import useTheme from '../hooks/useTheme';
import { useLocale } from '../i18n/LocaleContext';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { t } = useLocale();

  const navItems = [
    { label: t('nav.home'), to: '/' },
    { label: t('nav.services'), to: '/services' },
    { label: t('nav.work'), to: '/work' },
    { label: t('nav.analytics'), to: '/analytics' },
    { label: t('nav.pricing'), to: '/pricing' },
    { label: t('nav.contact'), to: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Announcement bar */}
      <div className="bg-surface border-b border-line text-center py-2 px-4">
        <span className="font-mono-ui text-[11px] tracking-wider text-faint">
          <span className="text-green-500">●</span> NEW — AI-POWERED ANALYTICS DASHBOARDS{' '}
          <Link
            to="/analytics"
            className="text-purple-500 font-semibold hover:text-pink-500 transition-colors"
          >
            / see it live →
          </Link>
        </span>
      </div>

      {/* Main bar */}
      <div
        className={`transition-all duration-300 ${
          isScrolled ? 'glass border-b border-line' : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo lockup with sync-bars signature */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <img
                src="/brand/logo-64.png"
                alt="SynchronicTech logo"
                className="relative w-9 h-9 rounded-lg object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <span className="font-display text-lg font-bold text-strong tracking-tight">
                Synchronic<span className="text-purple-500">Tech</span>
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `relative px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
                      isActive ? 'text-strong' : 'text-soft hover:text-strong'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      <span
                        className={`absolute bottom-1 left-3.5 right-3.5 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300 ${
                          isActive ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Theme toggle + CTA */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={toggle}
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
                className="w-9 h-9 rounded-lg border border-line bg-raise text-soft hover:text-strong hover:bg-raise2 flex items-center justify-center transition-all duration-200"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <Button
                onClick={() => navigate('/contact')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-5 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/25 group"
              >
                {t('cta.getDemo')}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* Mobile: theme toggle + menu */}
            <div className="md:hidden flex items-center gap-1">
              <button
                onClick={toggle}
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                className="text-strong p-2"
              >
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <button
                className="text-strong p-2"
                onClick={() => setIsMobileMenuOpen((v) => !v)}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-3 mb-2 space-y-1 glass rounded-xl border border-line">
              {navItems.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'text-strong bg-raise2'
                        : 'text-soft hover:text-strong hover:bg-raise2'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="px-4 pt-2">
                <Button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    navigate('/contact');
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                >
                  {t('cta.getDemo')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
